import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2 } from 'lucide-react';

function CivilianStep({ resume, saveResume, onNext, saving, registerPendingData }) {
  const [experiences, setExperiences] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (resume?.civilian_experience) {
      console.log('[CivilianStep] Loading from resume');
      setExperiences(resume.civilian_experience);
      setIsDirty(false);
    }
  }, [resume?.civilian_experience]);

  // Register pending data whenever experiences change
  useEffect(() => {
    if (isDirty && registerPendingData) {
      registerPendingData({ civilian_experience: experiences });
    }
  }, [experiences, isDirty, registerPendingData]);

  const addExperience = () => {
    const newExp = {
      company: '',
      job_title: '',
      location: '',
      start_month: 1,
      start_year: 2020,
      end_month: 1,
      end_year: 2024,
      description: '',
      is_current: false,
      is_volunteer: false
    };
    setExperiences([...experiences, newExp]);
    setIsDirty(true);
  };

  const removeExperience = (index) => {
    setExperiences(experiences.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const updateExperience = useCallback((index, field, value) => {
    setExperiences(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setIsDirty(true);
  }, []);

  return (
    <div className="space-y-6">
      <p className="text-slate-600">Add paid work or volunteer roles you have not already included in your military experience.</p>

      {experiences.map((exp, index) => (
        <Card key={index} className="p-6 border border-slate-200" data-testid={`civilian-exp-${index}`}>
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Experience {index + 1}</h3>
            <Button
              data-testid={`remove-exp-${index}-btn`}
              onClick={() => removeExperience(index)}
              variant="ghost"
              size="sm"
              className="text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-900 font-medium">{exp.is_volunteer ? 'Organization' : 'Company'}</Label>
                <Input
                  data-testid={`company-input-${index}`}
                  value={exp.company}
                  onChange={(e) => updateExperience(index, 'company', e.target.value)}
                  className="h-12 mt-2"
                  placeholder={exp.is_volunteer ? 'Organization Name' : 'Company Name'}
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">Job Title</Label>
                <Input
                  data-testid={`job-title-input-${index}`}
                  value={exp.job_title}
                  onChange={(e) => updateExperience(index, 'job_title', e.target.value)}
                  className="h-12 mt-2"
                  placeholder="Software Engineer"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-900 font-medium">Location</Label>
              <Input
                data-testid={`location-input-${index}`}
                value={exp.location}
                onChange={(e) => updateExperience(index, 'location', e.target.value)}
                className="h-12 mt-2"
                placeholder="San Francisco, CA"
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-slate-900 font-medium">Start Month</Label>
                <Input
                  data-testid={`start-month-input-${index}`}
                  type="number"
                  min="1"
                  max="12"
                  value={exp.start_month}
                  onChange={(e) => updateExperience(index, 'start_month', parseInt(e.target.value))}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">Start Year</Label>
                <Input
                  data-testid={`start-year-input-${index}`}
                  type="number"
                  min="1950"
                  max="2100"
                  value={exp.start_year}
                  onChange={(e) => updateExperience(index, 'start_year', parseInt(e.target.value))}
                  className="h-12 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">End Month</Label>
                <Input
                  data-testid={`end-month-input-${index}`}
                  type="number"
                  min="1"
                  max="12"
                  value={exp.end_month || 1}
                  onChange={(e) => updateExperience(index, 'end_month', parseInt(e.target.value))}
                  className="h-12 mt-2"
                  disabled={exp.is_current}
                />
              </div>
              <div>
                <Label className="text-slate-900 font-medium">End Year</Label>
                <Input
                  data-testid={`end-year-input-${index}`}
                  type="number"
                  min="1950"
                  max="2100"
                  value={exp.end_year || 2024}
                  onChange={(e) => updateExperience(index, 'end_year', parseInt(e.target.value))}
                  className="h-12 mt-2"
                  disabled={exp.is_current}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  data-testid={`is-current-checkbox-${index}`}
                  checked={exp.is_current}
                  onCheckedChange={(checked) => updateExperience(index, 'is_current', checked)}
                />
                <Label className="text-slate-700 text-sm">
                  {exp.is_volunteer ? 'I currently volunteer here' : 'I currently work here'}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  data-testid={`is-volunteer-checkbox-${index}`}
                  checked={exp.is_volunteer || false}
                  onCheckedChange={(checked) => updateExperience(index, 'is_volunteer', checked === true)}
                />
                <Label className="text-slate-700 text-sm">This was volunteer work</Label>
              </div>
            </div>

            <div>
              <Label className="text-slate-900 font-medium">Description</Label>
              <Textarea
                data-testid={`description-textarea-${index}`}
                value={exp.description}
                onChange={(e) => updateExperience(index, 'description', e.target.value)}
                className="mt-2 min-h-[100px]"
                placeholder="Describe your responsibilities and achievements..."
              />
            </div>
          </div>
        </Card>
      ))}

      <Button
        data-testid="add-civilian-exp-btn"
        onClick={addExperience}
        variant="outline"
        className="w-full border-dashed border-2 border-slate-300 text-slate-700 hover:bg-slate-50"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Experience
      </Button>
    </div>
  );
}

export default CivilianStep;